import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router';
import { useStore } from '@/app/StoreContext';
import { runtime } from '@/platform/runtime';
import { GuestRegistrationForm } from '@/components/auth/GuestRegistrationForm';
import { LettersBanner } from '@/components/round/LettersBanner';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { StatsCard } from '@/components/profile/StatsCard';
import { RecentGames } from '@/components/profile/RecentGames';
import { fadeSlide } from '@/styles/motion';
import { ru } from '@/i18n/ru';
import styles from './HomePage.module.css';

const KOT: readonly [string, string, string] = ['к', 'о', 'т'];

const AuthedHome = observer(function AuthedHome({ joinCode }: { joinCode: string | null }) {
  const { auth, onboarding, profile } = useStore();
  const nav = useNavigate();

  useEffect(() => { profile.load(); }, [profile]);

  useEffect(() => {
    if (joinCode) {
      nav(`/join?code=${encodeURIComponent(joinCode)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.authedPage}>
      {/* Header: kicker + nickname + avatar */}
      <header className={styles.authedHeader}>
        <div className={styles.authedGreeting}>
          <span className={styles.kicker}>{ru.home.welcomeBack}</span>
          <span className={styles.authedNick}>{auth.nickname}</span>
        </div>
        {/* Avatar → A4 Profile (D04); navigation wired in D04 */}
        <button
          className={styles.avatarBtn}
          onClick={() => { /* TODO: navigate to A4 profile screen (D04) */ }}
          aria-label="Профиль"
        >
          <Avatar name={auth.nickname ?? '?'} color={auth.avatarColor} size={46} />
        </button>
      </header>

      {/* Main action buttons */}
      <div className={styles.actions}>
        <Button variant="primary" onClick={() => nav('/create')} style={{ width: '100%' }}>
          {ru.home.createRoom}
        </Button>
        <Button variant="outline" onClick={() => nav('/join')} style={{ width: '100%' }}>
          {ru.home.joinByCode}
        </Button>
      </div>

      {profile.profile?.stats && (
        <StatsCard stats={profile.profile.stats} />
      )}

      {profile.profile?.history && profile.profile.history.length > 0 && (
        <RecentGames history={profile.profile.history} />
      )}

      <Button variant="ghost" onClick={() => onboarding.open()} className={styles.howTo}>
        {ru.onboarding.howToPlay}
      </Button>

    </div>
  );
});

export const HomePage = observer(function HomePage() {
  const { auth, onboarding } = useStore();
  const [searchParams] = useSearchParams();
  const joinCode = searchParams.get('join');

  useEffect(() => { document.title = 'Сквозь'; }, []);

  if (!auth.isAuthenticated) {
    return (
      <div className={styles.page}>
        {/* AppShell.Header уже показывает бренд+тему в браузере; в Mini App
            он скрыт (runtime.isMiniApp), и это единственная замена. */}
        {runtime.isMiniApp && (
          <header className={styles.header}>
            <span className={styles.wordmark}>Сквозь</span>
            <ThemeToggle />
          </header>
        )}

        <motion.div
          className={styles.bannerWrap}
          variants={fadeSlide}
          initial="hidden"
          animate="visible"
        >
          <LettersBanner letters={KOT} size="lg" />
        </motion.div>

        <h1 className={styles.heading}>Сквозь</h1>
        <p className={styles.lead}>Слово-маршрут за 90 секунд.</p>

        <div className={styles.spacer} />

        <Card className={styles.card}>
          <GuestRegistrationForm />
        </Card>

        <Button
          variant="ghost"
          onClick={() => onboarding.open()}
          className={styles.howTo}
        >
          {ru.onboarding.howToPlay}
        </Button>
      </div>
    );
  }

  return <AuthedHome joinCode={joinCode} />;
});
